import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  TimeSeriesScale,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import { MatProgressBarModule } from '@angular/material/progress-bar';

Chart.register(
  CategoryScale,
  BarElement,
  TimeSeriesScale,
  BarController,
  Title,
  Tooltip,
  Legend,
);

type RawDataType = { x: number; y: number; tooltip: string };

type ChartDataType = RawDataType[];

interface TooltipType extends TooltipItem<'bar'> {
  raw: RawDataType;
}

export type ProductionLossesChartInputData = {
  start: Date;
  end: Date;
  data: Array<{
    time: number; // 5 minute aggregates
    value: number; // uptime percentage (max 100)
  }>;
};

@Component({
  selector: 'thred-production-losses-chart',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule],
  templateUrl: './production-losses-chart.component.html',
  styleUrl: './production-losses-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductionLossesChartComponent implements OnChanges {
  @ViewChild('productionLossesChart') set canvas(canvas: ElementRef) {
    if (canvas) {
      this.myCanvas.set(canvas);
      this.createChart(canvas);
    }
  }

  myCanvas = signal<ElementRef | null>(null);
  private chart: Chart | undefined;

  @Input({ required: true }) data: ProductionLossesChartInputData | null = null;
  @Input() chartTitle = '';
  @Input() dataIntervalInMinutes = 5;
  @Input() unit: 'minute' | 'hour' = 'minute';
  @Input() loading = signal(false);

  ngOnChanges(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    const canvas = this.myCanvas();
    if (canvas) {
      this.createChart(canvas);
    }
  }

  createChart(canvas: ElementRef<HTMLCanvasElement>) {
    if (this.data) {
      const productionLossMap = new Map<
        number,
        { totalProduction: number; tooltip: string }
      >();
      for (const { time, value } of this.data.data) {
        const t = new Date(time);

        const mapped = productionLossMap.get(t.getTime());
        if (mapped) {
          mapped.totalProduction += value >= 0 ? value : 0;
        } else {
          const tEnd = new Date(t);
          tEnd.setMinutes(t.getMinutes() + this.dataIntervalInMinutes);
          const tooltip = `${t.getHours()}:${t
            .getMinutes()
            .toString()
            .padStart(2, '0')} - ${tEnd.getHours()}:${tEnd
            .getMinutes()
            .toString()
            .padStart(2, '0')} `;

          productionLossMap.set(t.getTime(), {
            tooltip,
            totalProduction: value,
          });
        }
      }
      const productionData: ChartDataType = [];
      const lossesData: ChartDataType = [];
      for (const [key, value] of productionLossMap.entries()) {
        productionData.push({
          x: key,
          y: value.totalProduction,
          tooltip: value.tooltip,
        });
        lossesData.push({
          x: key,
          y: 100 - value.totalProduction,
          tooltip: value.tooltip,
        });
      }

      this.chart = new Chart(canvas.nativeElement, {
        type: 'bar',
        data: {
          datasets: [
            {
              data: productionData,
              label: 'Production (%)',
              stack: 'a',
              backgroundColor: '#10645c',
              hoverBorderWidth: 2,
              barPercentage: 0.9,
              categoryPercentage: 1,
            },
            {
              data: lossesData,
              label: 'Losses (%)',
              stack: 'a',
              backgroundColor: '#ad1b1b',
              hoverBorderWidth: 2,
              barPercentage: 0.9,
              categoryPercentage: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                title: function ([context]: TooltipType[]) {
                  return context.raw.tooltip;
                },
              },
              displayColors: false,
            },
            title: {
              display: true,
              text: this.chartTitle,
              color: '#333333',
              font: {
                size: 15,
                weight: 'bold',
              },
            },
            legend: {
              display: false,
            },
          },
          animation: {
            duration: 0,
          },
          scales: {
            x: {
              type: 'time',
              time: {
                unit: this.unit,
              },
              title: {
                display: true,
                text: 'time',
              },
              ticks: {
                stepSize: this.dataIntervalInMinutes,
              },
            },
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    }
  }
}
